const { getElement } = window.BAO.Utils

const second = 1000
const minute = second * 60
const hour = minute * 60
const day = hour * 24
const week = day * 7

const privateKeyExpiryDuration = week * 2

/**
 * A Shopify Image product
 * @typedef {Object} GiftWithPurchaseGift
 *
 * @property {number} id
 * @property {number} variantId
 * @property {number} quantity
 * @property {string} handle
 */
/**
 * A Shopify Image product
 * @typedef {Object} GiftWithPurchaseRule
 *
 * @property {string} id
 * @property {string} type
 * @property {'>'|'>='|'='} operator
 * @property {string} value
 * @property {GiftWithPurchaseGift} gift
 * @property {number} discount
 */

class TypeRule {
  constructor (config, giftWithPurchase) {
    this.config = config
    this.giftWithPurchase = giftWithPurchase

    if (this.isPrivate) {
      this.maybeSaveKeyFromUrl()
    }
  }

  checkEligibility (operator) {
    throw new Error(`"checkEligibility" needs to be defined`)
  }

  maybeSaveKeyFromUrl () {
    const expiryDate = Date.now() + privateKeyExpiryDuration
    const searchParams = new URL(window.location).searchParams
    const hasAlreadySavedKey = this.savedKeys.some(
      ({ key }) => key === this.key,
    )

    if (searchParams.has(this.key) && !hasAlreadySavedKey) {
      window.localStorage.setItem(
        'gwp:keys',
        JSON.stringify([
          { key: this.key, expires: expiryDate },
          ...this.savedKeys,
        ]),
      )
    }
  }

  get isEligible () {
    throw new Error(`"get isEligible" needs to be defined`)
  }

  get freeGiftFormData () {
    return {
      id: this.config.gift.variantId,
      quantity: this.config.gift.quantity,
      properties: {
        [this.formDataPropertyKeys.claimed]: `${this.id}`,
        [this.formDataPropertyKeys.quantity]: `${this.config.gift.quantity}`,
        [this.formDataPropertyKeys.discount]: (
          this.config.discount / 100
        ).toFixed(2),
      },
    }
  }

  get hasBeenClaimed () {
    return this.cartState.items.some(item => {
      return (
        item.properties !== undefined &&
        Object.keys(item.properties).length > 0 &&
        item.properties[this.formDataPropertyKeys.claimed] !== undefined &&
        item.properties[this.formDataPropertyKeys.claimed] === `${this.id}`
      )
    })
  }

  get cartState () {
    return this.giftWithPurchase.cartState
  }

  get id () {
    return this.config.id
  }

  get formDataPropertyKeys () {
    return {
      claimed: '_Free Gift Rule ID',
      discount: '_Free Gift Rule Discount',
      quantity: '_Free Gift Rule Quantity',
    }
  }

  get lineItems () {
    if (!this.hasBeenClaimed) return []

    return this.cartState.items.filter(
      item =>
        item.properties[this.formDataPropertyKeys.claimed] === `${this.id}`,
    )
  }

  get isPrivate () {
    return this.config.isPrivate
  }

  get key () {
    return this.config.key
  }

  /** @returns {{ key: string; expires: number }[]} */
  get savedKeys () {
    try {
      const savedKeys = JSON.parse(window.localStorage.getItem('gwp:keys'))

      if (!Array.isArray(savedKeys)) {
        return []
      }

      const now = Date.now()
      return savedKeys.filter(key => key.expires > now)
    } catch (error) {
      return []
    }
  }

  get hasKeySaved () {
    return this.savedKeys.some(({ key }) => key === this.key)
  }
}

class PriceTypeRule extends TypeRule {
  checkEligibility (operator) {
    if (this.isPrivate && !this.hasKeySaved) {
      return false
    }

    switch (operator) {
      case '>=': {
        return this.totalPrice >= this.threshold
      }
      case '>': {
        return this.totalPrice > this.threshold
      }
      case '=': {
        return this.totalPrice === this.threshold
      }
      default:
        console.error(`Operator ${operator} unknown`)

        return false
    }
  }

  get isEligible () {
    if (this.isPrivate && !this.hasKeySaved) {
      return false
    }

    return this.checkEligibility(this.config.operator)
  }

  get threshold () {
    return this.config.value * Number(window.Shopify.currency.rate)
  }

  get totalPrice () {
    // if ('total_price' in this.cartState) return this.cartState.total_price

    return this.cartState.items.reduce((totalPrice, item) => {
      if (item.product_type !== 'Gift Cards') {
        return totalPrice + item.final_line_price
      } else {
        return totalPrice
      }
    }, 0)
  }
}

class NumberOfProductsTypeRule extends TypeRule {
  checkEligibility (operator) {
    if (this.isPrivate && !this.hasKeySaved) {
      return false
    }

    switch (operator) {
      case '>=': {
        return this.numberOfProducts >= this.config.value
      }
      case '>': {
        return this.numberOfProducts > this.config.value
      }
      case '=': {
        return this.numberOfProducts === this.config.value
      }
      default:
        throw new Error(`Operator ${operator} unknown`)
    }
  }

  get isEligible () {
    return this.checkEligibility(this.config.operator)
  }

  get numberOfProducts () {
    return this.cartState.items
      .filter(item => item.final_price > 0)
      .map(item => item.quantity)
      .reduce((totalQuantity, itemQuantity) => totalQuantity + itemQuantity, 0)
  }
}

class UniqueNumberOfProductsTypeRule extends TypeRule {
  checkEligibility (operator) {
    if (this.isPrivate && !this.hasKeySaved) {
      return false
    }

    switch (operator) {
      case '>=': {
        return this.uniqueNumberOfProducts >= this.config.value
      }
      case '>': {
        return this.uniqueNumberOfProducts > this.config.value
      }
      case '=': {
        return this.uniqueNumberOfProducts === this.config.value
      }
      default:
        throw new Error(`Operator ${operator} unknown`)
    }
  }

  get isEligible () {
    return this.checkEligibility(this.config.operator)
  }

  get uniqueNumberOfProducts () {
    return this.cartState.items.filter(item => item.final_price > 0).length
  }
}

class CollectionTypeRule extends TypeRule {
  checkEligibility (operator) {
    if (this.isPrivate && !this.hasKeySaved) {
      return false
    }

    switch (operator) {
      case '=': {
        return this.cartState.items.some(item => {
          return item.additionalData.collectionHandles.some(
            handle => handle === this.config.value,
          )
        })
      }
      default:
        throw new Error(`Operator ${operator} unknown`)
    }
  }

  get isEligible () {
    return this.checkEligibility(this.config.operator)
  }
}

window.BAO.GiftWithPurchase = window.BAO.GiftWithPurchase || {}

class GiftWithPurchase {
  constructor () {
    this.activeRules = this.rules.map(rule => this.initialiseRule(rule))

    this.isRunning = false

    this.onCartChange = this.onCartChange.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      this.onCartChange,
    )

    this.removeFreeGiftsThatHaveInactiveRules(this.cartState)

    this.log('constructor', {
      activeRules: this.activeRules,
      rules: this.rules,
      cartState: this.cartState,
    })
  }

  initialiseRule (rule) {
    switch (rule.type) {
      case 'Price':
        return new PriceTypeRule(rule, this)
      case 'Collection':
        return new CollectionTypeRule(rule, this)
      case 'Number of Products':
        return new NumberOfProductsTypeRule(rule, this)
      case 'Unique Number of Products':
        return new UniqueNumberOfProductsTypeRule(rule, this)
      default:
        throw new Error(`${rule.type} is an unrecognised GWP rule`)
    }
  }

  getNewlyEligibleRules () {
    return this.activeRules.filter(
      rule => rule.isEligible && !rule.hasBeenClaimed,
    )
  }

  getNewlyIneligibleRules () {
    return this.activeRules.filter(
      rule => !rule.isEligible && rule.hasBeenClaimed,
    )
  }

  hasBeenClaimed () {
    return (
      this.activeRules.filter(rule => rule.isEligible && rule.hasBeenClaimed)
        .length > 0
    )
  }

  async removeFreeGiftsThatHaveInactiveRules (state) {
    const freeGifts = this.getFreeGiftsFromCartState(state)
    if (freeGifts.length === 0) return freeGifts

    const activeRuleIds = this.activeRules.map(rule => rule.config.id)
    const itemsThatNeedToBeRemoved = freeGifts.filter(
      item => !activeRuleIds.includes(item.properties['_Free Gift Rule ID']),
    )

    if (itemsThatNeedToBeRemoved.length === 0) return []

    const newState = await this.cart.removeItems(
      itemsThatNeedToBeRemoved.map(i => i.key),
    )

    if ('sections' in newState) {
      getElement('push-cart, cart-item').renderSections(newState.sections)
    }

    return newState
  }

  getFreeGiftsFromCartState (state) {
    return state.items.filter(
      item => item?.properties['_Free Gift Rule ID'] !== undefined,
    )
  }

  async activateNewlyEligibleRules () {
    return this.cart.addItem(
      this.getNewlyEligibleRules().map(rule => rule.freeGiftFormData),
    )
  }

  async deactivateNewlyEligibleRules (render = true) {
    const sections = getElement('push-cart')
      ? window.BAO.sectionRenderer.pushCartSections
      : getElement('cart-items')
        ? window.BAO.sectionRenderer.cartSections
        : []

    const state = await this.cart.removeItems(
      this.getNewlyIneligibleRules().flatMap(rule =>
        rule.lineItems.map(lineItem => lineItem.key),
      ),
      { sections: sections.map(section => section.section) },
    )

    if ('sections' in state) {
      if (render) {
        getElement('push-cart, cart-item').renderSections(state.sections)
      } else {
        const toHide = this.getNewlyIneligibleRules().flatMap(rule =>
          rule.lineItems.map(lineItem => lineItem.key),
        )

        toHide.forEach(item => {
          getElement(
            `cart-item[data-key="${item}"]`,
          ).parentElement.ariaHidden = true
        })
      }
    }

    return state
  }

  async onCartChange (e) {
    if (!this.enabled) return
    if (this.isRunning) return

    this.isRunning = true

    this.log('onCartChange', {
      cartState: this.cartState,
      hasNewlyEligibleRules: this.hasNewlyEligibleRules,
      newlyEligibleRules: this.getNewlyEligibleRules(),
      hasNewlyIneligibleRules: this.hasNewlyIneligibleRules,
      newlyIneligibleRules: this.getNewlyIneligibleRules(),
    })

    document.body.dispatchEvent(
      new CustomEvent('gift-with-purchase:update', {
        bubbles: true,
        detail: this,
      }),
    )

    // if (this.hasNewlyEligibleRules) {
    //   await this.activateNewlyEligibleRules()
    // }

    if (this.hasNewlyIneligibleRules) {
      await this.deactivateNewlyEligibleRules(e.detail.gwpRender)
    }

    this.isRunning = false
  }

  /**
   * @param {string} groupName
   * @param {any[]} otherItems
   *
   * @returns {undefined}
   */
  log (groupName, ...otherItems) {
    if (!this.debug) return

    /* eslint-disable no-console */
    console.group(`GiftWithPurchase - ${groupName}`)

    otherItems.forEach(item => console.log(item))

    console.groupEnd()
    /* eslint-enable no-console */
  }

  /**
   * @returns {boolean}
   */
  get hasNewlyEligibleRules () {
    return this.getNewlyEligibleRules().length > 0
  }

  /**
   * @returns {boolean}
   */
  get hasNewlyIneligibleRules () {
    return this.getNewlyIneligibleRules().length > 0
  }

  /**
   * @returns {boolean}
   */
  get enabled () {
    return this.rules !== undefined && this.rules.length > 0
  }

  /**
   * @returns {GiftWithPurchaseRule[]}
   */
  get rules () {
    return window.BAO.GiftWithPurchase?.rules ?? []
  }

  get cart () {
    return window.BAO.Cart
  }

  /**
   * @returns {ShopifyCartState}
   */
  get cartState () {
    const baseState = JSON.parse(
      document.querySelector(`[data-cart-state]`).innerHTML,
    )
    const additionalItemData = JSON.parse(
      document.querySelector(`[data-cart-items-additional-data]`).innerHTML,
    )

    baseState.items.forEach(item => {
      item.additionalData = additionalItemData[item.key] || {}
    })

    return baseState
  }

  /**
   * @returns {boolean}
   */
  get debug () {
    return window.theme.isDevTheme
  }
}

window.BAO.GiftWithPurchase.Controller = new GiftWithPurchase()
